import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import { Alert, Button, Form, Modal, Spinner } from '../../../components/ui';
import { locationsService } from '../../../services/locationsService';
import { stockMovementService, type CatalogMaterial } from '../../../services/stockMovementService';
import type { BinMaterial, SiteWithLocations, StorageBin } from '../types';
import { BIN_CODE_MAX_LENGTH, suggestBinCode } from '../utils/addBin';
import { binMaterialLabel } from '../utils/siteDrillThrough';
import {
  binFormFrom,
  materialLockReason,
  OWNER_CHOICES,
  toBinInput,
  validateBinForm,
  type BinForm,
} from '../utils/structureForms';

interface BinFormModalProps {
  show: boolean;
  site: SiteWithLocations;
  locationId: string | null;
  /** The bin to edit, or null to add one to the location. */
  bin: StorageBin | null;
  /** materials:cost_manage: shows the unit cost field. */
  canManageCost: boolean;
  onHide: () => void;
  onSaved: (message: string) => void;
}

/** What the material dropdown lists: a catalogue hit, or the material the bin already holds. */
interface MaterialOption {
  id: string;
  label: string;
  unitOfMeasure: string | null;
}

const fromCatalog = (material: CatalogMaterial): MaterialOption => ({
  id: material.id,
  label: binMaterialLabel({ material: { ...material, type: material.type } }) ?? `Material ${material.id}`,
  unitOfMeasure: material.unitOfMeasure,
});

const fromBin = (material: BinMaterial): MaterialOption => ({
  id: material.id,
  label: binMaterialLabel({ material }) ?? `Material ${material.id}`,
  unitOfMeasure: material.unitOfMeasure,
});

const Required: React.FC = () => (
  <span className="tw-text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

/**
 * Adds a bin to a storage location, or edits one: its code, the material it is for, its owner
 * and the min/max band the Locations list flags against. A bin starts with no stock; stock
 * arrives through Adjust inventory (a receipt) or a count. The material can only change while
 * the bin is at zero, and a bin that has never moved stock can be removed.
 */
const BinFormModal: React.FC<BinFormModalProps> = ({ show, site, locationId, bin, canManageCost, onHide, onSaved }) => {
  const location = site.locations.find((candidate) => candidate.id === locationId) ?? null;
  const [form, setForm] = useState<BinForm>(() => binFormFrom(null, site.owner, ''));
  const [picked, setPicked] = useState<MaterialOption | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Every catalogue hit seen while this form is open, so a pick keeps its label after the search clears.
  const seen = useRef(new Map<string, MaterialOption>());

  useEffect(() => {
    if (!show) return;
    setForm(binFormFrom(bin, site.owner, suggestBinCode(location ?? undefined)));
    setPicked(bin?.material ? fromBin(bin.material) : null);
    seen.current = new Map();
    setAttempted(false);
    setBusy(null);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const searchCatalogue = useCallback(
    async (query: string) => {
      const found = (await stockMovementService.searchMaterials(query, site.id)).map(fromCatalog);
      for (const option of found) seen.current.set(option.id, option);
      return found;
    },
    [site.id]
  );

  if (!location) return null;

  const errors = validateBinForm(form, location, bin?.id ?? null);
  const shown = attempted ? errors : {};
  const lockReason = materialLockReason(bin);
  // ledgerVersion moves with every movement, so 0 means the bin has never held stock.
  const removeReason = bin && bin.ledgerVersion > 0 ? `${bin.code} has stock history, so it stays on the ledger` : null;
  const unit = picked?.unitOfMeasure ? ` (${picked.unitOfMeasure})` : '';

  const set = <K extends keyof BinForm>(key: K, value: BinForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setSaveError(null);
  };

  const save = async () => {
    setAttempted(true);
    if (Object.keys(errors).length) return;
    setBusy('save');
    setSaveError(null);
    try {
      const input = toBinInput(form, canManageCost);
      if (bin) await locationsService.updateBin(site.id, location.id, bin.id, input);
      else await locationsService.createBin(site.id, location.id, input);
      onSaved(bin ? `Saved ${input.code}.` : `Added ${input.code} to ${location.name}.`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save the bin');
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!bin) return;
    setBusy('remove');
    setSaveError(null);
    try {
      await locationsService.deleteBin(site.id, location.id, bin.id);
      onSaved(`Removed ${bin.code} from ${location.name}.`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to remove the bin');
      setBusy(null);
    }
  };

  const numberField = (key: 'minQuantity' | 'maxQuantity' | 'unitCost', label: string, placeholder: string) => (
    <Form.Group controlId={`bin-form-${key}`}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={form[key]}
        placeholder={placeholder}
        isInvalid={!!shown[key]}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => set(key, event.target.value)}
      />
      {shown[key] ? <Form.Control.Feedback type="invalid">{shown[key]}</Form.Control.Feedback> : null}
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={busy ? () => undefined : onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{bin ? `Edit ${bin.code}` : `Add a bin to ${location.name}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="tw-space-y-4">
        {saveError ? <Alert variant="danger" className="tw-mb-0">{saveError}</Alert> : null}

        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
          <Form.Group controlId="bin-form-code">
            <Form.Label>
              Bin code
              <Required />
            </Form.Label>
            <Form.Control
              autoFocus
              value={form.code}
              maxLength={BIN_CODE_MAX_LENGTH}
              placeholder="e.g. Bin 1"
              isInvalid={!!shown.code}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('code', event.target.value)}
            />
            {shown.code ? <Form.Control.Feedback type="invalid">{shown.code}</Form.Control.Feedback> : null}
          </Form.Group>
          <Form.Group controlId="bin-form-owner">
            <Form.Label>Owner</Form.Label>
            <Form.Select value={form.owner} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => set('owner', event.target.value)}>
              <option value="">Not recorded</option>
              {OWNER_CHOICES.map((owner) => (
                <option key={owner} value={owner}>
                  {owner}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        <div>
          <StandoutDropdown
            type="searchable"
            label="Material"
            options={picked ? [picked] : []}
            value={form.materialId}
            onChange={(value) => {
              set('materialId', value || '');
              setPicked(value ? (seen.current.get(value) ?? picked) : null);
            }}
            getValue={(option: MaterialOption) => option.id}
            formatOption={(option: MaterialOption) => option.label}
            fetchOptions={searchCatalogue}
            minCharsForSearch={2}
            placeholder="Search the catalogue"
            noOptionsMessage="No catalogue material matches that search"
            allowClear
            disabled={lockReason !== null}
          />
          <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">
            {lockReason ?? 'Leave it empty for a bin that will be assigned on its first receipt.'}
          </p>
        </div>

        <div className={`tw-grid tw-grid-cols-1 tw-gap-3 ${canManageCost ? 'sm:tw-grid-cols-3' : 'sm:tw-grid-cols-2'}`}>
          {numberField('minQuantity', `Minimum${unit}`, 'Not set')}
          {numberField('maxQuantity', `Maximum${unit}`, 'Not set')}
          {canManageCost ? numberField('unitCost', 'Unit cost ($)', 'Not set') : null}
        </div>
        <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
          The Locations list flags a bin below its minimum or over its maximum.
          {bin ? null : ' Stock is added afterwards, with Adjust inventory or a count.'}
        </p>
      </Modal.Body>
      <Modal.Footer>
        {bin ? (
          <Button
            variant="outline-danger"
            className="tw-mr-auto"
            onClick={() => void remove()}
            disabled={busy !== null || removeReason !== null}
            disabledReason={busy ? undefined : removeReason ?? undefined}
          >
            {busy === 'remove' ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash" aria-hidden="true" />}
            Remove bin
          </Button>
        ) : null}
        <Button variant="outline-secondary" onClick={onHide} disabled={busy !== null}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void save()} disabled={busy !== null}>
          {busy === 'save' ? <Spinner animation="border" size="sm" /> : null}
          {bin ? 'Save bin' : 'Add bin'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BinFormModal;
